import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Box, Button, Flex, Icons, Modal, Text, Theme } from 'baukasten';

import { useEscapeKey } from 'utils/hooks/useEscapeKey';

import { useCompanionWorkspaceContext } from '../CompanionWorkspaceContext';
import messages from './messages';
import type { ThreadMeta } from '../types';
import { groupThreads } from '../utils/groupThreads';
import { ThreadRow } from './ThreadRow';

const Panel = styled(Flex)`
  position: absolute;
  inset: 0;
  z-index: 1;
  background-color: ${Theme.getColor('white')};
`;

const SearchField = styled.input`
  width: 100%;
  border: 1px solid ${Theme.getColor('grey-light-100')};
  border-radius: 8px;
  outline: none;
  padding: ${Theme.getBaseUnit(1)} ${Theme.getBaseUnit(2)}
    ${Theme.getBaseUnit(1)} ${Theme.getBaseUnit(8)};
  font-family: inherit;
  font-size: ${Theme.getFontSize(14)};
  color: ${Theme.getColor('navy')};

  &:focus {
    border-color: ${Theme.getColor('lilac')};
  }
`;

const SearchIcon = styled(Box)`
  position: absolute;
  left: ${Theme.getBaseUnit(2)};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  pointer-events: none;
`;

const List = styled(Box)`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

export const ThreadHistory = () => {
  const { formatMessage, locale } = useIntl();
  const workspace = useCompanionWorkspaceContext();
  const [query, setQuery] = useState('');
  const [pendingDelete, setPendingDelete] = useState<ThreadMeta | null>(null);
  // baukasten's Modal has no Escape handling of its own
  useEscapeKey(() => setPendingDelete(null));

  const groups = groupThreads(workspace.threads, query, {
    formatMessage,
    locale,
  });
  const isEmpty =
    !workspace.threadsLoading && !workspace.threadsError && !groups.length;

  return (
    <Panel col px={3} pb={3}>
      <Box position="relative" py={3}>
        <SearchIcon>
          <Icons.Search color="grey-dark" size="14px" noPadding />
        </SearchIcon>
        <SearchField
          autoFocus
          value={query}
          aria-label={formatMessage(messages.threadSearch)}
          placeholder={formatMessage(messages.threadSearch)}
          onChange={event => setQuery(event.target.value)}
        />
      </Box>
      <List>
        {workspace.threadsError && (
          <Flex gap={2} alignItems="center">
            <Text.SmallDescription color="grey-dark">
              {formatMessage(messages.threadsLoadFailed)}
            </Text.SmallDescription>
            <Button
              type="button"
              variant="text"
              size="s"
              onClick={workspace.refreshThreads}
            >
              {formatMessage(messages.threadsRetry)}
            </Button>
          </Flex>
        )}
        {isEmpty && (
          <Text.SmallDescription color="grey-dark">
            {formatMessage(messages.threadsEmpty)}
          </Text.SmallDescription>
        )}
        {groups.map(group => (
          <Box key={group.label} mb={2}>
            <Box pl={2} pb={1}>
              <Text.SmallDescription color="grey-dark">
                {group.label}
              </Text.SmallDescription>
            </Box>
            {group.items.map(thread => (
              <ThreadRow
                key={thread.id}
                thread={thread}
                active={thread.id === workspace.chatId}
                onOpen={() => workspace.openThread(thread.id)}
                onRename={name => workspace.renameThread(thread.id, name)}
                onDelete={() => setPendingDelete(thread)}
              />
            ))}
          </Box>
        ))}
      </List>
      {pendingDelete && (
        <Modal
          isModalOpen
          maxWidth="480px"
          title={formatMessage(messages.threadDeleteTitle)}
          ariaLabel={formatMessage(messages.threadDeleteTitle)}
          onCloseModal={() => setPendingDelete(null)}
          footer={
            <Flex gap={2} justifyContent="flex-end">
              <Button variant="text" onClick={() => setPendingDelete(null)}>
                {formatMessage(messages.threadDeleteCancel)}
              </Button>
              <Button
                color="danger"
                onClick={() => {
                  workspace.deleteThread(pendingDelete.id);
                  setPendingDelete(null);
                }}
              >
                {formatMessage(messages.threadDelete)}
              </Button>
            </Flex>
          }
        >
          <Text>
            {formatMessage(messages.threadDeleteConfirm, {
              name:
                pendingDelete.name || formatMessage(messages.threadUntitled),
            })}
          </Text>
        </Modal>
      )}
    </Panel>
  );
};
